import { SERVER_BASE_URL } from '../config/api.config';

/**
 * Singleton WebSocket service for managing a single shared connection
 * Replaces react-use-websocket to fix connection sharing issues
 */
class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectTimeout = null;
    this.messageHandlers = new Map(); // stream -> Set of handlers
    this.statusChangeListeners = new Set(); // listeners for connection status changes
    this.isConnecting = false;
    this.isConnected = false;
    this.subscriptions = new Map(); // stream -> params (track active subscriptions)
  }

  // Add listener for connection status changes
  addStatusChangeListener(listener) {
    this.statusChangeListeners.add(listener);
  }

  // Remove listener for connection status changes
  removeStatusChangeListener(listener) {
    this.statusChangeListeners.delete(listener);
  }

  // Notify all status change listeners
  notifyStatusChange(status) {
    this.statusChangeListeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('Error in status change listener:', error);
      }
    });
  }

  connect() {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      return; // Already connected or connecting
    }

    if (this.isConnecting) {
      return; // Connection attempt in progress
    }

    this.isConnecting = true;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = SERVER_BASE_URL.replace(/^https?:\/\//, '');
    const url = `${protocol}//${host}/api/ws`;

    // Notify listeners that we're connecting
    this.notifyStatusChange(this.reconnectAttempts > 0 ? 'Reconnecting' : 'Connecting');

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.isConnecting = false;
      this.isConnected = true;
      this.reconnectAttempts = 0;

      // Notify listeners that we're connected
      this.notifyStatusChange('Connected');

      // Resubscribe to all streams
      this.subscriptions.forEach((params, stream) => {
        this.subscribe(stream, params);
      });
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        // Route message to appropriate handlers
        if (message.type === 'data' && message.stream) {
          const handlers = this.messageHandlers.get(message.stream);

          if (handlers && handlers.size > 0) {
            handlers.forEach(handler => {
              try {
                handler(message.data);
              } catch (handlerError) {
                console.error(`Error in message handler for ${message.stream}:`, handlerError);
              }
            });
          }
        } else if (message.type === 'error') {
          console.error('WebSocket error:', message.error);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    this.ws.onclose = (event) => {
      this.isConnecting = false;
      this.isConnected = false;

      // Notify listeners that we're disconnected
      this.notifyStatusChange('Disconnected');

      // Exponential backoff reconnection
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        const delay = Math.min(Math.pow(2, this.reconnectAttempts) * 1000, 30000);
        this.reconnectAttempts++;

        this.reconnectTimeout = setTimeout(() => {
          this.connect();
        }, delay);
      } else {
        console.error('Max reconnection attempts reached');
      }
    };
  }

  subscribe(stream, params = {}) {
    // Track subscription for reconnection (Map automatically handles duplicates)
    this.subscriptions.set(stream, params);

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        const message = {
          type: 'subscribe',
          stream: stream,
          data: params,
        };

        this.ws.send(JSON.stringify(message));
      } catch (error) {
        console.error('Failed to subscribe:', error);
      }
    }
  }

  unsubscribe(stream) {
    // Remove from subscriptions
    this.subscriptions.delete(stream);

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        const message = {
          type: 'unsubscribe',
          stream: stream,
        };

        this.ws.send(JSON.stringify(message));
      } catch (error) {
        console.error('Failed to unsubscribe:', error);
      }
    }
  }

  addMessageHandler(stream, handler) {
    if (!this.messageHandlers.has(stream)) {
      this.messageHandlers.set(stream, new Set());
    }
    this.messageHandlers.get(stream).add(handler);
  }

  removeMessageHandler(stream, handler) {
    const handlers = this.messageHandlers.get(stream);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.messageHandlers.delete(stream);
      }
    }
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.isConnecting = false;
    this.isConnected = false;
  }

  getConnectionStatus() {
    if (!this.ws) return 'Disconnected';

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'Connecting';
      case WebSocket.OPEN:
        return 'Connected';
      case WebSocket.CLOSING:
        return 'Closing';
      case WebSocket.CLOSED:
        return 'Disconnected';
      default:
        return 'Unknown';
    }
  }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;
